<template>
  <NuxtLayout
    name="generic"
    :header="pageData.header"
    :footer="pageData.footer"
    :page-title="pageData.title"
  >
    <main
      :class="{
        'pricing-page': true,
      }"
    >
      <div
        :class="{
          'pricing-hero bg-www-color-neutral-off-white pb-10 pt-180px text-center text-www-color-neutral-medium-dark lg:pb-[110px]': true,
        }"
      >
        <PortableText
          v-if="pageData.heroContent.standardPortableText"
          :portable-data="pageData.heroContent.standardPortableText"
          :class="{
            'mb-12': true,
          }"
        />
      </div>

      <PlanSection
        v-if="pageData.plans"
        v-bind="pageData.plans"
      />

      <div class="flex justify-center py-6">
        <Button
          v-if="pageData.comparePlansButton"
          :button-props="{
            text: pageData.comparePlansButton,
            _type: 'button',
            style: 'secondary',
          }"
          @click="scrollToSection"
        />
      </div>

      <SectionBuilder
        v-if="pageData.content"
        v-bind="pageData.content"
        :class="{
          'text-www-color-neutral-medium-dark': true,
        }"
      />

      <PricingComparison
        ref="pricing-comparison"
        :compare-plans-header="pageData.comparePlansHeader"
        v-bind="{ ...pageData.plans, _type: 'highlightedPlans' }"
        page-type="education"
      />

      <CTABanner
        v-if="ctaBannerData"
        :data="ctaBannerData"
      />
    </main>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import type { CTABannerRefQueryResult, EducationPricing } from '~/sanity/types';
import PortableText from '~/components/Atmos/PortableText/PortableText.vue';
import PlanSection from '~/components/Atmos/Plan/PlanSection.vue';
import Button from '~/components/Atmos/Button/Button.vue';
import SectionBuilder from '~/components/Atmos/SectionBuilder/SectionBuilder.vue';
import CTABanner from '~/components/Organisms/CTABanner/CTABanner.vue';
import PricingComparison from '~/components/Misc/PricingComparison.vue';
import { educationPricingQuery } from '~/queries/pages/PageEducationPricing.query';
import type {
  ResolvedPlanEntry,
  ResolvedPlansQueryResult,
  StructuredDataOptions,
} from '~/utils/structuredDataTypes';
import { buildPricingPlanStructuredData } from '~/utils/structuredDataBuilder';
import { educationPricingResolvedQuery } from '~/queries/pages/PageEducationPricingResolved.query';
import * as AssertThat from '~/utils/AssertThat';

type EducationPricingPageQueryResult = EducationPricing & {
  ctaBanner?: {
    reference?: CTABannerRefQueryResult | null;
  } | null;
};

const { path: currentPathname } = useRoute();

const { data: pageDataNullish } = await useWwwSanityQueryNonNull<EducationPricingPageQueryResult>(
  educationPricingQuery,
  {
    currentPathname,
  }
);
const pageData = AssertThat.getNonNullRefOrThrow(
  pageDataNullish,
  'Education Pricing page data is null or undefined'
);

const { data: resolvedPlansDataNullish } = await useWwwSanityQueryNonNull<ResolvedPlansQueryResult>(
  educationPricingResolvedQuery,
  {
    currentPathname,
  }
);

const resolvedPlansData = AssertThat.getNonNullRefOrThrow(
  resolvedPlansDataNullish,
  'Resolved plans data for Education Pricing page is null or undefined'
);

const defaultCurrency = useState<string>('default-currency');
const targetSection = useTemplateRef('pricing-comparison');

const pageTitle = pageData.value.htmlTitle || pageData.value.title;
const pageDescription = pageData.value.ogDescription || pageData.value.description;
const highlightedPlans = resolvedPlansData.value.plans ?? [];
const ctaBannerData = computed(() => pageData.value.ctaBanner?.reference ?? undefined);

const resolvedPlans: ResolvedPlanEntry[] = highlightedPlans
  .map((highlightedPlan) => {
    const planRefId = highlightedPlan.selectPlan?.reference?._ref;
    const resolvedPlan = highlightedPlan.resolvedPlan;

    if (!planRefId || !resolvedPlan?.name) {
      return null;
    }

    return {
      plan: resolvedPlan,
      planRefId,
      resolvedPlanPrices: highlightedPlan.resolvedPlanPrices,
    };
  })
  .filter((resolvedEntry): resolvedEntry is ResolvedPlanEntry => resolvedEntry !== null);

const perPlanStructuredData: StructuredDataOptions[] = resolvedPlans.map(
  ({ plan, planRefId, resolvedPlanPrices }) =>
    buildPricingPlanStructuredData({
      plan,
      planRefId,
      defaultCurrency,
      resolvedPlanPrices,
    })
);

const scrollToSection = () => {
  targetSection.value?.$el.scrollIntoView({ behavior: 'smooth' });
};

usePageHead({
  _id: pageData.value?._id,
  description: pageDescription,
  ogImage: pageData.value?.ogImage?.asset ?? pageData.value?.cardImage?.asset,
  title: pageTitle,
  excludeFromExternalSearch: pageData.value?.excludeFromExternalSearch,
  twitterCard: 'summary',
  structuredData: [
    {
      schemaType: 'OfferCatalog',
      title: pageTitle,
      description: pageDescription,
      enabled: true,
    },
    ...perPlanStructuredData,
  ],
});
</script>

<style>
.pricing-hero p {
  margin-bottom: 7px !important;
}
</style>
